import { ChangeEvent, FC, ReactNode } from 'react';
import {
    Box,
    Flex,
    Heading,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuGroup,
    MenuItem,
    MenuList,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tag,
    Text,
    Tooltip,
    useToast,
} from '@chakra-ui/react';
import { RiAccountCircleLine } from 'react-icons/ri';
import { BiLogOut } from 'react-icons/bi';

import icons, { TIcon } from 'shared/icons';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useGetInternationalCompaniesQuery, useUpdateUserCompanyMutation } from 'core/API/authApi';
import { IInternationalCompany } from 'types';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'helpers/languages/translations';
import { useMultiLanguagesHandler } from 'contexts/MultiLanguagesContext';
import { ViewIcon } from '@chakra-ui/icons';

const iconDefaultProps = {
    height: 'auto',
    display: 'inline',
    width: '24px',
    mr: '4',
};

const PageHeader: FC<{
    icon: { name: TIcon; props?: any };
    pageTitle: string;
    readonly?: boolean;
    children?: ReactNode;
}> = ({ icon, pageTitle, readonly, children }) => {
    const { setLocale, locale } = useMultiLanguagesHandler();
    const userInfo = useGetUserInfoSelector();
    const companyInfo = useGetCompanyInfoSelector();
    const { data, isUninitialized, isLoading } = useGetInternationalCompaniesQuery(undefined, {
        skip: userInfo && !userInfo.is_international,
    });
    const [updateUserCompany, { isLoading: isUpdateCompanyLoading }] = useUpdateUserCompanyMutation({
        fixedCacheKey: 'auth-update-user-company',
    });
    const translate = useTranslation();
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    // todo: align left the texts
    const renderAvatarMenu = () => {
        return (
            <Flex>
                <Menu>
                    <MenuButton
                        as={IconButton}
                        aria-label="Account"
                        icon={<RiAccountCircleLine size={24} color="black" />}
                        variant="ghost"
                        ml="16px"
                        data-testid="profile_button"
                    />
                    <MenuList zIndex="popover">
                        <MenuGroup title={userInfo ? userInfo.email : ''} borderBottom="1px solid #CAC5C4" pb="16px">
                            <Flex
                                flexDirection="column"
                                gridGap="16px"
                                m="0 16px 0 16px"
                                pb="16px"
                                borderBottom="1px solid #CAC5C4"
                            >
                                <Text display="block" fontSize="16px" fontWeight="700">
                                    {translate('settings.lang')}
                                </Text>
                                <RadioGroup onChange={setLocale} value={locale} data-testid="language-selector">
                                    <Stack direction="column">
                                        <Radio value="en" data-value="en">
                                            {translate('settings.lang.en')}
                                        </Radio>
                                        <Radio value="fr" data-value="fr">
                                            {translate('settings.lang.fr')}
                                        </Radio>
                                    </Stack>
                                </RadioGroup>
                            </Flex>
                        </MenuGroup>
                        <MenuGroup>
                            <MenuItem as="a" href={`${process.env.REACT_APP_CDM_API}/auth/logout`} icon={<BiLogOut />}>
                                {translate('settings.logout')}
                            </MenuItem>
                        </MenuGroup>
                    </MenuList>
                </Menu>
            </Flex>
        );
    };

    const renderInternationalCompaniesSelection = (internationalCompanies: IInternationalCompany[]) => {
        const options: any[] = [];
        const handleChange = async (e: ChangeEvent<HTMLSelectElement>) => {
            try {
                await updateUserCompany({ id: e.target.value });
                navigate(location.pathname, { replace: true });
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        internationalCompanies.forEach((intCompany) => {
            intCompany.companies.forEach((c) => {
                options.push(<option key={c.id} value={c.id}>{`${c.country_name}_${c.id}`}</option>);
            });
        });

        const isSU = userInfo?.email?.toLowerCase()?.includes('_su@');
        return (
            <Flex>
                <Select
                    disabled={isUpdateCompanyLoading || isSU}
                    onChange={intentionalyFloatingPromiseReturn(handleChange)}
                    value={companyInfo ? `${companyInfo.id}` : ''}
                    size="sm"
                    bg="white"
                >
                    {options}
                </Select>
            </Flex>
        );
    };

    const Icon = icons[icon.name];

    return (
        <HStack px="32px" py="26px" justifyContent="space-between">
            <Flex alignItems="center">
                <Icon {...iconDefaultProps} {...(icon.props ? icon.props : {})} />
                <Box display="flex" alignItems="center">
                    <Heading as="h1" fontSize={'2xl'} fontWeight={'semibold'}>
                        {pageTitle}
                    </Heading>
                    {readonly && (
                        <Box minHeight="100%" display="flex" alignItems="flex-end" pt="3px">
                            <Tooltip label={translate('header.tooltip.readonly')} aria-label="Read only">
                                <Tag ml="5px" size="sm" colorScheme="gray" cursor="default">
                                    {translate('header.tag.readonly')}
                                </Tag>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Flex>
            <Flex flex="1" px="24px">
                {children}
            </Flex>
            {!isUninitialized &&
                !isLoading &&
                data &&
                data.internationalCompanies &&
                data.internationalCompanies.length > 0 &&
                data.internationalCompanies[0].companies.length > 1 &&
                renderInternationalCompaniesSelection(data.internationalCompanies)}
            {renderAvatarMenu()}
        </HStack>
    );
};

export default PageHeader;
